/**
 * Shared facet utilities and types used across SSR and client
 * This is the single source of truth for facet-related code
 */
import { getRegionTagLabel, getRegionTagType, type RegionTagType } from './region-tag-labels';

// Attributes that need facet data
export const FACET_ATTRIBUTES = [
  'location',
  'remoteness',
  'companyIndustry',
  'companyId',
  'jobType',
  'normalisedRoleLevel',
  'normalisedTitle',
  'requiredLanguages',
  'benefits',
  'skillsAndTechnologies',
  'field',
  'educationLevel',
  'peopleManagementTier',
  // TEC-482: materialized hierarchy + recruiting-region tags (e.g. filtering "APAC" returns
  // onsite Sydney jobs alongside "Remote - APAC"/"Remote - Global" postings). The backend always
  // computes this, safe to request unconditionally; it's just empty until the gazetteer is
  // seeded and jobs have been (re-)enriched. Which UI shows it is a separate, client-side
  // decision: see the dev_enable_new_location_system cookie in FilterModal.tsx.
  'regionTags',
] as const;

export interface FacetValue {
  value: string;
  label: string;
  count: number;
  isRefined: boolean;
  /** Set only for the `regionTags` attribute, lets the UI group continent/region/country/Finland values instead of one flat list. */
  regionTagType?: RegionTagType;
  /**
   * Set only for the `regionTags` attribute: how many of this bucket's jobs are also
   * remoteness=Remote. Requires a companion facet query scoped by remoteness (see
   * mergeRegionTagsRemoteCounts below and its call site in pages/index/+data.ts), so it's
   * undefined - not 0 - wherever that hasn't run.
   */
  remoteCount?: number;
}

export interface FacetsData {
  [attribute: string]: FacetValue[];
}

/**
 * Build facet filters for a query, optionally excluding one attribute
 * to avoid narrowing options by the same facet.
 * Note: Values should already be normalized to match Algolia capitalization
 * before calling this function.
 */
export function buildFacetFilters(
  allFilters: Record<string, string[]>,
  excludeAttribute?: string
): (string | string[])[] {
  const result: (string | string[])[] = [];
  Object.entries(allFilters || {}).forEach(([key, values]) => {
    if (!Array.isArray(values) || values.length === 0) return;
    if (key === excludeAttribute) return; // Don't narrow by the same facet
    if (values.length === 1) {
      result.push(`${key}:${values[0]}`);
    } else {
      result.push(values.map((v) => `${key}:${v}`));
    }
  });
  return result;
}

/**
 * Fetch all facets in a single batched request
 * THIS SHOULD ONLY BE CALLED SERVER-SIDE (during SSR)
 */
export async function fetchAllFacets(
  searchClient: any,
  indexName: string,
  query: string = '',
  filters: Record<string, string[]> = {}
): Promise<FacetsData> {
  try {
    // Create batched queries for all facets
    const queries = FACET_ATTRIBUTES.map((attribute) => {
      const facetFilters = buildFacetFilters(filters, attribute);
      return {
        indexName,
        params: {
          hitsPerPage: 0,
          attributesToRetrieve: [],
          attributesToHighlight: [],
          facets: [attribute],
          facetFilters,
          query: query || '',
          maxValuesPerFacet: 100,
        },
      };
    });

    console.log('[ALGOLIA] DEPRECATED fetchAllFacets - making', queries.length, 'separate API calls (SHOULD BE AVOIDED)');
    const response = await searchClient.search(queries);
    console.log('[ALGOLIA] fetchAllFacets complete - DO NOT USE THIS FUNCTION');
    const facetsData: FacetsData = {};

    // Process results
    response.results?.forEach((result: any, index: number) => {
      const attribute = FACET_ATTRIBUTES[index];
      const facetObj = result.facets?.[attribute] || {};
      const currentRefinements = filters[attribute] || [];

      const items = Object.entries(facetObj)
        .map(([value, count]) => ({
          value,
          label: value,
          count: Number(count || 0),
          // Use case-insensitive comparison for isRefined check
          isRefined: isFacetValueRefined(value, currentRefinements),
        }))
        .sort((a, b) => b.count - a.count);

      // Add refined values that might have 0 count in current query (using case-insensitive matching)
      currentRefinements.forEach((val) => {
        if (!items.find((i) => i.value.toLowerCase() === val.toLowerCase())) {
          // Try to find the capitalized version from facet data
          const capitalizedValue = Object.keys(facetObj).find(
            fv => fv.toLowerCase() === val.toLowerCase()
          ) || val;
          items.unshift({ value: capitalizedValue, label: capitalizedValue, count: 0, isRefined: true });
        }
      });

      facetsData[attribute] = items;
    });

    console.log('[ALGOLIA] fetchAllFacets fetched facets for', Object.keys(facetsData).length, 'attributes');
    return facetsData;
  } catch (error) {
    console.error('[ALGOLIA] fetchAllFacets error:', error);
    return {};
  }
}

/**
 * Matches a lowercase value from URL to the actual capitalized facet value from Algolia.
 * This allows case-insensitive matching while preserving the original capitalization.
 * @param lowercaseValue The lowercase value from URL
 * @param facetValues Array of available facet values from Algolia
 * @returns The matching capitalized value, or the original lowercase value if no match found
 */
export function matchFacetValueToAlgolia(
  lowercaseValue: string,
  facetValues: FacetValue[]
): string {
  if (!lowercaseValue || !facetValues || facetValues.length === 0) {
    return lowercaseValue;
  }
  
  const lower = lowercaseValue.toLowerCase();
  const match = facetValues.find(fv => fv.value.toLowerCase() === lower);
  return match ? match.value : lowercaseValue;
}

/**
 * Normalizes filter values from URLs (which are lowercase) to match capitalized Algolia facet values.
 * Uses available facet data to find the correct capitalization.
 * @param filters Filters from URL (with lowercase values)
 * @param facetsData Available facet data from Algolia (with capitalized values)
 * @returns Filters with values normalized to match Algolia capitalization
 */
export function normalizeFiltersFromUrl(
  filters: Record<string, string[]>,
  facetsData: FacetsData
): Record<string, string[]> {
  const normalized: Record<string, string[]> = {};
  
  Object.entries(filters).forEach(([attribute, values]) => {
    const facetValues = facetsData[attribute] || [];
    normalized[attribute] = values.map(val => 
      matchFacetValueToAlgolia(val, facetValues)
    );
  });
  
  return normalized;
}

/**
 * Checks if a facet value is refined, using case-insensitive comparison.
 * @param value The facet value to check (from Algolia, capitalized)
 * @param currentRefinements Array of current refinement values (may be lowercase from URL or capitalized)
 * @returns True if the value is refined
 */
export function isFacetValueRefined(
  value: string,
  currentRefinements: string[]
): boolean {
  if (!value || !currentRefinements || currentRefinements.length === 0) {
    return false;
  }
  
  const valueLower = value.toLowerCase();
  return currentRefinements.some(ref => ref.toLowerCase() === valueLower);
}

/**
 * Extract facets from Algolia search results.
 * Uses FACET_ATTRIBUTES order so server and client produce identical facet key order.
 */
export function extractFacetsFromSearchResults(
  searchResults: any,
  currentFilters: Record<string, string[]> = {},
  locale: 'en' | 'fi' | 'sv' = 'en'
): FacetsData {
  const facetsData: FacetsData = {};

  if (!searchResults || !searchResults.facets) {
    return facetsData;
  }

  const rawFacets = searchResults.facets as Record<string, Record<string, number>>;

  // Iterate in fixed attribute order so server and client output is identical
  for (const attribute of FACET_ATTRIBUTES) {
    // `location` facet counts are requested against `locationList` (the real per-city array
    // field, see typesenseFilterAttribute in build-typesense-search-params.ts) so a job valid
    // in multiple cities counts toward each city instead of forming one joined "A, B, C" bucket.
    // Read from that key but keep exposing it under `location` for existing UI consumers.
    const rawKey = attribute === 'location' ? 'locationList' : attribute;
    const facetObj = rawFacets[rawKey];
    if (facetObj == null) continue;

    const currentRefinements = currentFilters[attribute] || [];

    const items = Object.entries(facetObj)
      .map(([value, count]) => ({
        value,
        // TEC-482: regionTags values are gazetteer place ids / region codes, not display text, 
        // every other facet's value already doubles as its own label.
        label: attribute === 'regionTags' ? getRegionTagLabel(value, locale) : value,
        count: Number(count || 0),
        isRefined: isFacetValueRefined(value, currentRefinements),
        ...(attribute === 'regionTags' ? { regionTagType: getRegionTagType(value) } : {}),
      }))
      .sort((a, b) => b.count - a.count);

    currentRefinements.forEach((val) => {
      if (!items.find((i) => i.value.toLowerCase() === val.toLowerCase())) {
        const capitalizedValue =
          Object.keys(facetObj).find((fv) => fv.toLowerCase() === val.toLowerCase()) || val;
        items.unshift({
          value: capitalizedValue,
          label: attribute === 'regionTags' ? getRegionTagLabel(capitalizedValue, locale) : capitalizedValue,
          count: 0,
          isRefined: true,
          ...(attribute === 'regionTags' ? { regionTagType: getRegionTagType(capitalizedValue) } : {}),
        });
      }
    });

    facetsData[attribute] = items;
  }

  return facetsData;
}

/**
 * TEC-482 follow-up: stamps `remoteCount` onto each `regionTags` facet value from a companion
 * facet query scoped to remoteness=Remote (the same filters as the main query, plus that one
 * extra clause - see pages/index/+data.ts, which builds `rawRemoteRegionTagsFacet` via
 * `performSsrJobSearch`). A region's blended count alone can't tell the location picker how many
 * of its jobs are actually remote there vs physically onsite: remote-anywhere jobs inherit every
 * recruiting-region tag (see the FACET_ATTRIBUTES comment above), which is exactly what makes the
 * two numbers worth showing separately. No-op when there's no regionTags facet or no companion
 * data (e.g. the new location system is disabled for this request).
 */
export function mergeRegionTagsRemoteCounts(
  facetsData: FacetsData,
  rawRemoteRegionTagsFacet: Record<string, number> | undefined | null
): FacetsData {
  if (!rawRemoteRegionTagsFacet || !facetsData.regionTags) return facetsData;
  return {
    ...facetsData,
    regionTags: facetsData.regionTags.map((item) => ({
      ...item,
      remoteCount: Number(rawRemoteRegionTagsFacet[item.value] || 0),
    })),
  };
}


